/* ==========================================================================
   FILE: services/memory.service.js
   IAMONEAI - Memory Service
   Memories, goals, knowledge
   ========================================================================== */

import { client } from '../api';

// ==========================================================================
// CACHE SETTINGS
// ==========================================================================

const CACHE_TTL = {
  memories: 120000, // 2 minutes
  goals: 120000,
};

// ==========================================================================
// MEMORY SERVICE
// ==========================================================================

const memoryService = {
  /**
   * Get all memories for user
   * @param {string} iin 
   * @param {Object} options
   * @param {string} options.type - Filter by type
   * @param {string} options.context - Filter by context
   * @param {number} options.limit - Max results
   * @returns {Promise<Object>}
   */
  async getAll(iin, options = {}) {
    let endpoint = `/memory/${iin}`;
    const params = [];
    
    if (options.type) params.push(`type=${options.type}`);
    if (options.context) params.push(`context=${options.context}`);
    if (options.limit) params.push(`limit=${options.limit}`);
    
    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }

    return client.get(endpoint, {
      cache: true,
      cacheTTL: CACHE_TTL.memories,
    });
  },

  /**
   * Add a new memory
   * @param {string} iin 
   * @param {Object} memory
   * @param {string} memory.content - Memory content
   * @param {string} memory.type - Memory type (episodic/semantic/procedural)
   * @param {string} memory.context - Context (personal/work/family)
   * @returns {Promise<Object>}
   */
  async add(iin, memory) {
    return client.post('/memory', {
      iin,
      ...memory,
    }, {
      cache: false,
      offline: 'queue',
      priority: 'normal',
    });
  },

  /**
   * Search memories semantically
   * @param {string} iin 
   * @param {string} query 
   * @param {Object} options
   * @returns {Promise<Object>}
   */
  async search(iin, query, options = {}) {
    return client.post('/memory/search', {
      iin,
      query,
      ...options,
    }, {
      cache: true,
      cacheTTL: 60000, // 1 minute
    });
  },

  /**
   * Get goals only
   * @param {string} iin 
   * @param {Object} options
   * @param {string} options.status - Filter by status (active/completed/paused)
   * @param {string} options.context - Filter by context
   * @returns {Promise<Object>}
   */
  async getGoals(iin, options = {}) {
    return this.getAll(iin, {
      type: 'goal',
      ...options,
    });
  },

  /**
   * Get recent memories
   * @param {string} iin 
   * @param {number} limit 
   * @returns {Promise<Object>}
   */
  async getRecent(iin, limit = 10) {
    return this.getAll(iin, { limit });
  },

  /**
   * Get memories by type
   * @param {string} iin 
   * @param {string} type - episodic/semantic/procedural/goal/belief/preference
   * @returns {Promise<Object>}
   */
  async getByType(iin, type) {
    return this.getAll(iin, { type });
  },
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export { memoryService };
export default memoryService;
